<template>
  <v-dialog
    v-model="isOpen"
    max-width="800px"
    @click:outside="closeModal()">
    <v-card>
      <v-card-title>
        <span class="text-h5">Upload CSV or XLSX File</span>
      </v-card-title>

      <v-card-text>
        <v-file-input
          :key="componentKey"
          v-model="selectedFile"
          label="Choose CSV or XLSX file"
          accept=".csv, .xlsx"
          dense
          outlined
          hide-details
          @change="handlePreview"
        />
      </v-card-text>

      <v-card-text>
        <div class="d-flex justify-end">
          <v-btn
            class="text-decoration-underline"
            text
            small
            color="primary"
            @click="downloadTemplate"
          >
            Download Template XLSX
          </v-btn>
        </div>
      </v-card-text>

      <v-alert
        v-if="messages"
        :type="messages_type || 'info'"
        dense
        text
      >
        {{ messages }}
      </v-alert>

      <v-data-table
        v-if="previewData.length > 0"
        :headers="filteredHeaders"
        :items="previewData"
        :expanded.sync="errorExpanded"
        :loading="loading"
        :hide-default-footer="true"
        :items-per-page="-1"
        fixed-header
        item-key="Model"
        class="elevation-1 mt-3"
        show-expand
        style="max-height: 300px; overflow-y: scroll;"
      >
        <template v-slot:item="{ item }">
          <tr :class="{ '': !item.valid }">
            <td>
              <v-icon
                @click="toggleExpand(item)">
                {{ isExpanded(item) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </td>
            <td
              v-for="header in filteredHeaders"
              :key="header.value"
            >
              {{ item[header.value] }}
            </td>
          </tr>
        </template>

        <template v-slot:expanded-item="{ item }">
          <td
            v-if="item.errorMessage && item.errorMessage.length"
            :colspan="headers.length">
            <v-alert
              :type="messages_type"
              dense
              text
            >
              {{ item.errorMessage.join(', ') }}
            </v-alert>
          </td>
        </template>
      </v-data-table>

      <v-card-actions>
        <v-spacer />
        <v-btn
          color="error"
          text
          @click="closeModal">
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          text
          :disabled="!canUpload"
          :class="{ 'upload-disabled': !canUpload }"
          :loading="loading"
          @click="handleUpload">
          Upload
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ProductProvider from '@/resources/ProductProvider'
import { mapActions } from 'vuex/dist/vuex.common'
import * as XLSX from 'xlsx'

const productService = new ProductProvider()

export default {
  data () {
    return {
      headers: [
        { text: 'MODEL (6 DIGIT)', value: 'MODEL (6 DIGIT)' },
        { text: 'GENDER', value: 'GENDER' },
        { text: 'GLOBAL CATEGORY', value: 'GLOBAL CATEGORY' },
        { text: 'GARMENT TYPE', value: 'GARMENT TYPE' },
        { text: 'CERTIFICATE OF ORIGIN ( FORM D/FORM E )', value: 'CERTIFICATE OF ORIGIN ( FORM D/FORM E )' },
        { text: 'HS CODE', value: 'HS CODE' },
        { text: 'Is Error', value: 'valid' }
      ],
      isOpen: true,
      loading: false,
      selectedFile: null,
      previewData: [],
      errorExpanded: [],
      componentKey: 0,
      messages: '',
      messages_type: 'info'
    }
  },
  computed: {
    canUpload () {
      return !this.loading
        && this.previewData.length > 0
        && this.previewData.every((row) => row.valid)
        && this.messages_type !== 'error'
    },
    filteredHeaders () {
      return this.headers.filter((h) => h.value !== 'valid')
    }
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar'
    }),
    isEmpty (val) {
      return !val || val.trim() === ''
    },
    toggleExpand (item) {
      this.errorExpanded = this.errorExpanded.includes(item)
        ? this.errorExpanded.filter((i) => i !== item)
        : [...this.errorExpanded, item]
    },
    isExpanded (item) {
      return this.errorExpanded.includes(item)
    },
    getError (row) {
      const messages = []

      const requiredFields = {
        'MODEL (6 DIGIT)': 'Model is required'
      }

      for (const [field, errorMsg] of Object.entries(requiredFields)) {
        if (this.isEmpty(row[field])) {
          messages.push(errorMsg)
        }
      }

      return messages
    },
    closeModal () {
      this.isOpen = false
      this.resetForm()
      this.$emit('close')
    },
    downloadTemplate () {
      try {
        window.open('/files/Product-Information-Template.xlsx', '_blank')
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      }
    },
    findDuplicateModels (data) {
      const modelCount = {}
      const duplicates = new Set()

      data.forEach((row) => {
        const model = row['MODEL (6 DIGIT)']
        if (model) {
          modelCount[model] = (modelCount[model] || 0) + 1
        }
      })

      Object.entries(modelCount).forEach(([model, count]) => {
        if (count > 1) {
          duplicates.add(model)
        }
      })

      return duplicates
    },
    handlePreview () {
      this.loading = true
      this.messages = null
      this.messages_type = ''
      if (!this.selectedFile) {
        this.messages = 'No file selected. Please choose a CSV file.'
        this.messages_type = 'error'
        this.resetForm()
        this.loading = false
        return
      }

      this.readFile(this.selectedFile)
      this.loading = false
    },
    readFile (file) {
      if (!(file instanceof Blob)) {
        this.messages = 'Invalid file format.'
        this.messages_type = 'error'
        this.loading = false
        return
      }

      const reader = new FileReader()
      const fileName = file.name.toLowerCase()

      reader.onload = (e) => {
        try {
          let workbook

          if (fileName.endsWith('.xlsx')) {
            const data = new Uint8Array(e.target.result)

            workbook = XLSX.read(data, {
              type: 'array'
            })
          } else if (fileName.endsWith('.csv')) {
            const text = e.target.result

            workbook = XLSX.read(text, {
              type: 'string'
            })
          } else {
            throw new Error('Unsupported file type')
          }

          const firstSheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[firstSheetName]

          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            defval: '',
            raw: false
          })

          this.processExcelData(jsonData)
        } catch (err) {
          console.error(err)
          this.messages = 'Unable to read file. Please check format.'
          this.messages_type = 'error'
        } finally {
          this.loading = false
        }
      }

      if (fileName.endsWith('.xlsx')) {
        reader.readAsArrayBuffer(file)
      } else {
        reader.readAsText(file)
      }
    },
    processExcelData (rows) {
      const requiredHeaders = this.headers
        .map((h) => h.text)
        .filter((h) => h !== 'Is Error' && h !== '')

      if (rows.length === 0) {
        this.messages = 'The uploaded file contains no data.'
        this.messages_type = 'error'
        return
      }

      const excelHeaders = Object.keys(rows[0])

      const missingColumns = requiredHeaders.filter(
        (h) => !excelHeaders.includes(h)
      )

      if (missingColumns.length > 0) {
        this.messages = `Invalid file. Missing columns: ${missingColumns.join(', ')}`
        this.messages_type = 'error'
        this.previewData = []
        return
      }

      this.previewData = rows.map((row) => {
        const errorMessage = this.getError(row)

        return {
          ...row,
          valid: errorMessage.length === 0,
          errorMessage
        }
      })

      this.duplicateModels = this.findDuplicateModels(this.previewData)

      if (this.duplicateModels.size > 0) {
        this.messages = `Duplicate models detected: ${[...this.duplicateModels].join(', ')}`
        this.messages_type = 'error'
        return
      }

      this.errorExpanded = this.previewData.filter((v) => !v.valid)

      if (this.errorExpanded.length > 0) {
        this.messages = 'Some rows contain errors.'
        this.messages_type = 'error'
      }

      this.loading = false
    },
    async handleUpload () {
      if (!this.canUpload) {
        return
      }

      try {
        this.loading = true

        const payload = this.previewData.map((item) => ({
            model: item['MODEL (6 DIGIT)'],
            gender: item['GENDER'].toLowerCase(),
            globalCategory: item['GLOBAL CATEGORY'],
            garmentType: item['GARMENT TYPE'].toLowerCase(),
            certificateOfOrigin: item['CERTIFICATE OF ORIGIN ( FORM D/FORM E )']?.toLowerCase(),
            hsCode: item['HS CODE']
          }))
        const { data } = await productService.importProductInformation(payload)

        if (data) {
          this.setSnackbar({
            value: true,
            message: 'Successfully imported products.',
            type: 'success'
          })
          this.closeModal()
          this.$emit('imported')
        }
      } catch (error) {
        console.error(error)
        this.messages = error.message
        this.messages_type = 'error'
      } finally {
        this.loading = false
      }
    },
    resetForm () {
      this.selectedFile = null
      this.messages_type = null
      this.previewData = []
      this.messages = ''
      this.componentKey++
    }
  }
}
</script>

<style scoped>
.v-alert {
  bottom: 0px;
  margin: 0 auto;
}
</style>
